import { useMemo } from 'react';

import { CountryEntity, CurrencyType } from 'api/types/entity';
import { FileUpload } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { FileType } from 'libs/ui';
import {
  Field,
  MAX_FILES,
} from 'modules/onboarding/common/forms/withdrawalAccountsForm';

interface Props {
  index: number;
  countriesLoading?: boolean;
  countries: CountryEntity[];
  files?: FileUpload[];
  onAddFiles?: (file: File[], index: number) => void;
  onRemoveFile?: (file: FileUpload) => void;
}

export const Fields = ({
  index,
  countriesLoading,
  countries,
  files,
  onAddFiles,
  onRemoveFile,
}: Props) => {
  const { t } = useTranslation();
  const name: `withdrawalAccounts.${number}` = `withdrawalAccounts.${index}`;

  const filteredFiles = useMemo(
    () => files?.filter((v) => v.data === index),
    [files, index]
  );

  return (
    <>
      <Field.Autocomplete
        label={t('onboarding.withdrawalAccounts.bankCountry.label')}
        loading={countriesLoading}
        name={`${name}.bankCountry`}
        optionLabelKey="name"
        options={countries}
        optionValueKey="id"
        placeholder={t('onboarding.withdrawalAccounts.bankCountry.placeholder')}
      />

      <Field.Autocomplete
        label={t('onboarding.withdrawalAccounts.currency.label')}
        loading={countriesLoading}
        name={`${name}.currency`}
        optionLabelKey="label"
        options={Object.values(CurrencyType).map((v) => ({
          value: v,
          label: v,
        }))}
        optionValueKey="value"
        placeholder={t('onboarding.withdrawalAccounts.currency.placeholder')}
        variant="select"
      />

      <Field.Text
        label={t('onboarding.withdrawalAccounts.swiftCode.label')}
        name={`${name}.swiftCode`}
        placeholder={t('onboarding.withdrawalAccounts.swiftCode.placeholder')}
      />

      <Field.Text
        label={t('onboarding.withdrawalAccounts.bankName.label')}
        name={`${name}.bankName`}
        placeholder={t('onboarding.withdrawalAccounts.bankName.placeholder')}
      />

      <Field.Text
        label={t('onboarding.withdrawalAccounts.bankAddress.label')}
        name={`${name}.bankAddress`}
        placeholder={t('onboarding.withdrawalAccounts.bankAddress.placeholder')}
      />

      <Field.Text
        label={t('onboarding.withdrawalAccounts.checkingAccount.label')}
        name={`${name}.checkingAccount`}
        placeholder={t(
          'onboarding.withdrawalAccounts.checkingAccount.placeholder'
        )}
      />

      <Field.Text
        showOptionalMark
        label={t(
          'onboarding.withdrawalAccounts.correspondentBankSwiftCode.label'
        )}
        name={`${name}.correspondentBankSwiftCode`}
        placeholder={t(
          'onboarding.withdrawalAccounts.correspondentBankSwiftCode.placeholder'
        )}
      />

      <Field.Text
        showOptionalMark
        label={t('onboarding.withdrawalAccounts.correspondentBankName.label')}
        name={`${name}.correspondentBankName`}
        placeholder={t(
          'onboarding.withdrawalAccounts.correspondentBankName.placeholder'
        )}
      />

      <Field.Text
        showOptionalMark
        label={t(
          'onboarding.withdrawalAccounts.correspondentBankAccountNumber.label'
        )}
        name={`${name}.correspondentBankAccountNumber`}
        placeholder={t(
          'onboarding.withdrawalAccounts.correspondentBankAccountNumber.placeholder'
        )}
      />

      <Field.FilesUpload
        files={filteredFiles}
        fileTypes={[FileType.pdf]}
        maxFiles={MAX_FILES}
        name={`${name}.ownerConfirmationFileIds`}
        title={t('onboarding.withdrawalAccounts.documentTitle')}
        onAddFiles={(f) => onAddFiles?.(f, index)}
        onRemoveFile={onRemoveFile}
      />
    </>
  );
};
